import React, { useState, useMemo } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const COLORS = ["#da7756", "#5b8def", "#50c878", "#f5a623", "#bd6bd6", "#45b7d1"];

function extractTableData(children: React.ReactNode): { headers: string[]; rows: string[][] } {
  const headers: string[] = [];
  const rows: string[][] = [];

  React.Children.forEach(children, (child: any) => {
    if (!child?.props?.children) return;
    const tag = child.type || child.props?.node?.tagName;
    const isHead = tag === "thead" || child.props?.node?.tagName === "thead";

    React.Children.forEach(child.props.children, (tr: any) => {
      if (!tr?.props?.children) return;
      const cells: string[] = [];
      React.Children.forEach(tr.props.children, (td: any) => {
        const text = extractText(td);
        cells.push(text);
      });
      if (isHead || (headers.length === 0 && rows.length === 0)) {
        headers.push(...cells);
      } else {
        rows.push(cells);
      }
    });
  });
  return { headers, rows };
}

function extractText(node: any): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node?.props) return "";
  if (node.props.children) {
    return React.Children.toArray(node.props.children).map(extractText).join("");
  }
  return "";
}

function parseNum(s: string): number | null {
  const cleaned = s.replace(/[,%$\s]/g, "");
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}|^\d{1,2}\/\d{1,2}\/\d{2,4}|^(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i;

function isDateLike(values: string[]): boolean {
  return values.filter((v) => DATE_RE.test(v.trim())).length > values.length * 0.5;
}

function isNumericCol(values: string[]): boolean {
  return values.filter((v) => parseNum(v) !== null).length > values.length * 0.5;
}

interface ChartInfo {
  labelIdx: number;
  numericIdxs: number[];
  isTimeSeries: boolean;
  data: Record<string, any>[];
}

function detectChart(headers: string[], rows: string[][]): ChartInfo | null {
  if (headers.length < 2 || rows.length < 2) return null;

  const colValues = headers.map((_, i) => rows.map((r) => r[i] || ""));
  const numericFlags = colValues.map(isNumericCol);
  const numericIdxs = numericFlags.map((f, i) => (f ? i : -1)).filter((i) => i >= 0);
  if (numericIdxs.length === 0) return null;

  // Find best label column: prefer date-like, then first non-numeric
  let labelIdx = colValues.findIndex((v, i) => !numericFlags[i] && isDateLike(v));
  if (labelIdx < 0) labelIdx = numericFlags.indexOf(false);
  if (labelIdx < 0) labelIdx = 0; // fallback: use first col even if numeric

  const isTimeSeries = isDateLike(colValues[labelIdx]);

  const data = rows.map((row) => {
    const entry: Record<string, any> = { label: row[labelIdx] || "" };
    numericIdxs.forEach((i) => {
      if (i !== labelIdx) entry[headers[i]] = parseNum(row[i]) ?? 0;
    });
    return entry;
  });

  const activeNumericIdxs = numericIdxs.filter((i) => i !== labelIdx);
  if (activeNumericIdxs.length === 0) return null;

  return { labelIdx, numericIdxs: activeNumericIdxs, isTimeSeries, data };
}

function formatTick(v: any): string {
  if (typeof v !== "number") return v;
  if (v >= 1e9) return (v / 1e9).toFixed(1) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return v.toFixed(v % 1 === 0 ? 0 : 2);
}

export default function SmartTable({ children }: { children: React.ReactNode }) {
  const [showChart, setShowChart] = useState(false);
  const { headers, rows } = useMemo(() => extractTableData(children), [children]);
  const chart = useMemo(() => detectChart(headers, rows), [headers, rows]);

  const ChartComponent = chart?.isTimeSeries ? LineChart : BarChart;
  const DataComponent = chart?.isTimeSeries ? Line : Bar;

  return (
    <div className="smart-table">
      {chart && (
        <div className="chart-toggle">
          <button className={!showChart ? "active" : ""} onClick={() => setShowChart(false)}>📋 Table</button>
          <button className={showChart ? "active" : ""} onClick={() => setShowChart(true)}>📊 Chart</button>
        </div>
      )}
      {showChart && chart ? (
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <ChartComponent data={chart.data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E5E0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} angle={chart.data.length > 10 ? -45 : 0} textAnchor={chart.data.length > 10 ? "end" : "middle"} height={chart.data.length > 10 ? 80 : 30} />
              <YAxis tickFormatter={formatTick} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: any) => formatTick(v)} />
              {chart.numericIdxs.length > 1 && <Legend />}
              {chart.numericIdxs.map((idx, i) => (
                <DataComponent
                  key={headers[idx]}
                  type="monotone"
                  dataKey={headers[idx]}
                  {...(chart.isTimeSeries
                    ? { stroke: COLORS[i % COLORS.length], strokeWidth: 2, dot: { r: 3 } }
                    : { fill: COLORS[i % COLORS.length] })}
                />
              ))}
            </ChartComponent>
          </ResponsiveContainer>
        </div>
      ) : (
        <table>
          <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((row, i) => <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </div>
  );
}
